import { Directive, EventEmitter, HostBinding, Input, OnInit, Output, Self } from '@angular/core';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { Feature } from 'geojson';
import * as _ from 'lodash';
import {
  Control,
  Evented,
  FullscreenControl,
  IControl,
  LngLatBoundsLike,
  Map,
  NavigationControl,
  ScaleControl
} from 'mapbox-gl';
import { filter, first, tap } from 'rxjs/operators';

import { LJAMapComponent } from './map';

export interface ControlOptions {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export class ControlBase implements OnInit {
  protected MapboxControl: Control;

  controlInstance;
  options: ControlOptions;

  constructor(@Self() protected _mapComponent: LJAMapComponent) {}

  ngOnInit() {
    this._mapComponent.mapLoad
      .pipe(
        filter(m => !!m),
        first(),
        tap(() => {
          if (!this.MapboxControl) {
            throw new Error(
              'Control Directives must specify a `MapboxControl`. No `MapboxControl`.'
            );
          }
        })
      )
      .subscribe(map => {
        const ctrl = new (Function.prototype.bind.call(this.MapboxControl, null, this.options))();
        map.addControl(ctrl, (this.options || {}).position);
        this.controlInstance = ctrl;
        this.onAdd(map);
      });
  }

  protected onAdd(map: Map) {}
}

export interface NavigationControlOptions extends ControlOptions {
  showCompass?: boolean;
  showZoom?: boolean;
}

@Directive({
  selector: 'lja-map[ljaNavigationControl]'
})
export class LJANavigationControlDirective extends ControlBase {
  @HostBinding('class.lja-navigation-control')
  cssClass = true;
  protected MapboxControl = <any>NavigationControl;
  @Input('ljaNavigationControl')
  options: NavigationControlOptions; // tslint:disable-line
}

@Directive({
  selector: 'lja-map[ljaFullscreenControl]'
})
export class LJAFullscreenControlDirective extends ControlBase {
  @HostBinding('class.lja-fullscreen-control')
  cssClass = true;
  protected MapboxControl = <any>FullscreenControl;
  @Input('ljaFullscreenControl')
  options: ControlOptions; // tslint:disable-line
}

export interface GeocoderControlOptions extends ControlOptions {
  accessToken: string;
  zoom?: number; // Defaults to 16
  flyTo?: boolean;
  placeholder?: string;
  proximity?: { latitude: number; longitude: number };
  trackProximity?: boolean;
  bbox?: LngLatBoundsLike;
  types?:
    | 'country'
    | 'region'
    | 'postcode'
    | 'district'
    | 'place'
    | 'locality'
    | 'neighborhood'
    | 'address'
    | 'poi';
  country?: string; // Limit results to one or more countries. Permitted values are ISO 3166 alpha 2 country codes separated by commas
  minLength?: number;
  limit?: number;
  language?: string;
  filter?: (feature: GeoJSON.Feature) => boolean;
  localGeocoder?: (query: string) => GeoJSON.FeatureCollection[];
}

@Directive({
  selector: 'lja-map[ljaGeocoderControl]'
})
export class LJAGeocoderControlDirective extends ControlBase {
  @HostBinding('class.lja-geocoder-control')
  cssClass = true;

  protected MapboxControl = <any>MapboxGeocoder;

  @Input('ljaGeocoderControl')
  options: GeocoderControlOptions; // tslint:disable-line
  @Output()
  geocoderResult = new EventEmitter<Feature>();
  @Output()
  geocoderClear = new EventEmitter();

  onAdd(map) {
    if (this.controlInstance) {
      this.controlInstance.on('result', ({ result }) => this.geocoderResult.emit(result));
      this.controlInstance.on('clear', () => this.geocoderClear.emit());
    }
  }
}

export interface HomeControlOptions extends ControlOptions {
  bounds: LngLatBoundsLike;
}

export class HomeControl extends Evented implements IControl {
  private _container: HTMLElement;
  private _map: Map;

  public options: HomeControlOptions;

  constructor(options?: Partial<HomeControlOptions>) {
    super();
    this.options = { ..._.omitBy(options, _.isNil) };
  }

  onAdd(map) {
    const button = document.createElement('button');
    const container = (this._container = document.createElement('div'));

    this._map = map;

    button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-home';
    button.type = 'button';
    button.setAttribute('aria-label', 'Home');

    button.addEventListener(
      'click',
      _.bind(function() {
        this.options.bounds && this._map.fitBounds(this.options.bounds);
      }, this)
    );

    container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    container.appendChild(button);

    return container;
  }

  onRemove() {
    this._container.parentNode.removeChild(this._container);
    this._map = null;
  }

  getDefaultPosition() {
    return 'top-right';
  }

  setOptions(options: Partial<HomeControlOptions>) {
    this.options = { ...this.options, ...options };
  }
}

@Directive({
  selector: 'lja-map[ljaHomeControl]'
})
export class LJAHomeControlDirective extends ControlBase {
  @HostBinding('class.lja-home-control')
  cssClass = true;
  protected MapboxControl = <any>HomeControl;
  @Input('ljaHomeControl')
  options: HomeControlOptions; // tslint:disable-line
}

export interface ScaleControlOptions extends ControlOptions {
  maxWidth?: number;
  unit?: 'imperial' | 'metric' | 'nautical';
}

@Directive({
  selector: 'lja-map[ljaScaleControl]'
})
export class LJAScaleControlDirective extends ControlBase {
  @HostBinding('class.lja-scale-control')
  cssClass = true;
  protected MapboxControl = <any>ScaleControl;
  @Input('ljaScaleControl')
  options: ScaleControlOptions; // tslint:disable-line
}
